import { Box, Stack, Typography } from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';

import { QUESTIONS } from '@/benefits-flow/screener/questions';
import QuestionField from '@/benefits-flow/screener/QuestionField';
import { TalkInput } from '@/karehero-app/explore/Chat';
import Screen from '@/karehero-app/explore/Screen';
import {
  JOURNEY,
  PERSON,
  WAFFLE,
} from '@/karehero-app/explore/benefits-calculator/BenefitsCalculator.content';
import {
  Delta,
  Note,
  ProposalRow,
  WorkingBanner,
} from '@/karehero-app/explore/benefits-calculator/Parts';

// Section D. The brain-dump, and the only section where the async constraint is
// load-bearing. Capture is static by the technical plan: a note is uploaded,
// read off the request path, and the answers come back later. So the sequence
// here is not question then answer, it is submit, carry on, and reconcile.
//
// The frames deliberately keep the taps running while the note is being read.
// That is the whole argument: latency is covered by the questions the note was
// never going to answer, and by the time it lands the user has moved on. Early
// stopping and async capture turn out to be complements.

const noop = () => undefined;

export const D1Invite = () => (
  <Screen
    title={WAFFLE.invite.title}
    footer={<TalkInput typePrompt={WAFFLE.invite.type} />}
  >
    <Typography variant='body2' color='text.secondary'>
      {WAFFLE.invite.body}
    </Typography>
  </Screen>
);
D1Invite.displayName = 'D1Invite';

export const D2Recording = () => (
  <Screen
    title={WAFFLE.recording.title}
    subtitle={WAFFLE.recording.hint}
    footer={
      <Button ariaLabel={WAFFLE.recording.stop} isFullWidth>
        {WAFFLE.recording.stop}
      </Button>
    }
  >
    <Stack sx={{ alignItems: 'center', pt: 2 }} spacing={3}>
      <TalkInput typePrompt={WAFFLE.invite.type} state='listening' />
      <Typography variant='h3'>{WAFFLE.recording.elapsed}</Typography>
    </Stack>
  </Screen>
);
D2Recording.displayName = 'D2Recording';

// The frame the whole section exists for: the queue is running and the user is
// answering something else. If this reads as fine, async capture is fine.
export const D3Meanwhile = () => (
  <Screen back>
    <Stack spacing={2}>
      <Box>
        <WorkingBanner label={WAFFLE.meanwhile.banner} />
      </Box>
      <Typography variant='body2' color='text.secondary'>
        {WAFFLE.meanwhile.body}
      </Typography>
      <QuestionField
        question={QUESTIONS['care-recipient-savings-held-in-bank-accounts']}
        recipientName={PERSON.name}
        value={undefined}
        onChange={noop}
      />
    </Stack>
  </Screen>
);
D3Meanwhile.displayName = 'D3Meanwhile';

export const D4Landed = () => (
  <Screen
    title={WAFFLE.landed.title}
    subtitle={WAFFLE.landed.body}
    footer={
      <Button ariaLabel={WAFFLE.landed.cta} isFullWidth>
        {WAFFLE.landed.cta}
      </Button>
    }
  >
    <Note title='What you said' body={WAFFLE.transcript} icon='message' />
  </Screen>
);
D4Landed.displayName = 'D4Landed';

// Five proposals, each traceable to a clause of the note. The quoted line under
// each answer is the anti-fabrication guardrail made visible: if a proposal has
// no quote it has no business being here.
export const D5Proposals = () => (
  <Screen
    title={WAFFLE.review.title}
    subtitle={WAFFLE.review.subtitle}
    footer={
      <Button ariaLabel={WAFFLE.review.confirmAll} isFullWidth>
        {WAFFLE.review.confirmAll}
      </Button>
    }
  >
    <Stack spacing={1}>
      {WAFFLE.proposals.map((proposal) => (
        <ProposalRow
          key={proposal.question}
          question={proposal.question}
          value={proposal.value}
          said={proposal.said}
          saidPrefix={WAFFLE.review.saidPrefix}
        />
      ))}
    </Stack>
  </Screen>
);
D5Proposals.displayName = 'D5Proposals';

// What the note did not settle stays a gap. Shown alongside what it did settle,
// because a mode that only reports its wins is not checkable.
export const D6Gaps = () => (
  <Screen title='What it could not tell us'>
    <Stack spacing={1.5}>
      {WAFFLE.gaps.map((gap) => (
        <Note
          key={gap}
          title='Still a gap'
          body={gap}
          tone='warn'
          icon='circle-question'
        />
      ))}
    </Stack>
  </Screen>
);
D6Gaps.displayName = 'D6Gaps';

export const D7Moved = () => (
  <Screen title={WAFFLE.done.title} subtitle={WAFFLE.done.body}>
    <Delta before={JOURNEY.taps} after={{ ...JOURNEY.taps, ...JOURNEY.note }} />
  </Screen>
);
D7Moved.displayName = 'D7Moved';
